/**
 * Typed wrappers over the HTTP tool gateway for the `/score/:matchId`
 * editor (web UI Phase 2 — score editor chunk 2/2). The symmetric
 * sibling of `lineup-api.ts`.
 *
 * The SPA NEVER calls the MCP server or Supabase directly. Every call
 * here goes through `POST /api/tool/:tool_name` (the gateway), which
 * forces `team_id` from the JWT, role-checks, rate-limits, and audits.
 * `report_score` is allowlisted for both `head` + `co` captains in
 * `src/api/tool-gateway.ts` — no allowlist change was needed.
 *
 * AGENTS.md RULE 3 lives at the SERVER (`src/mcp/tools/report_score.ts`):
 * `dry_run` is REQUIRED, `dry_run=false` needs the matching
 * `confirmation_token`, and double-submits dedupe on
 * `(match_id, score_hash)`. This module just exposes the two phases as
 * typed calls; the page wires the human-confirm gate around them.
 *
 * The gateway scrubs the real MCP error text and preserves only the
 * `error.code` (`CONFIRMATION_MISMATCH`, `BAD_INPUT`, `UPSTREAM_DOWN`,
 * …) — see `lineup-api.ts` for the full envelope contract. We reuse the
 * SAME `unwrapToolPayload` from `lineup-api.ts` (web + bot must agree on
 * the unwrap) and the SAME `LineupToolError` shape (re-exported here as
 * `ScoreToolError`; the normalized {code,httpStatus,message} error is
 * identical for both editors — no second error class).
 */

import { ApiError, apiPost } from "./api";
import { LineupToolError, unwrapToolPayload } from "./lineup-api";

// The normalized gateway error is structurally identical for both
// editors. Re-export under a score-local name for symmetry + readable
// call sites; it IS `LineupToolError` (one error contract, not two).
export { LineupToolError as ScoreToolError };

// ─── Wire types — mirror src/mcp/tools/report_score.ts contracts ───────
//
// Narrow + local; the shapes the page renders, not the full server
// interfaces. See `src/mcp/tools/report_score.ts` +
// `src/lib/wtta-score.ts` for the authoritative definitions.

export type LineNumber = 1 | 2 | 3 | 4;

/** Per-line games as supplied by the captain. */
export type ScoreLineInput = {
  line_number: LineNumber;
  /** Games won by the captain's team on this line. */
  home_games: number;
  /** Games won by the opponent on this line. */
  away_games: number;
};

export type ScoreResult = "home_win" | "away_win" | "tie";

export type ComputedLine = {
  line_number: LineNumber;
  home_games: number;
  away_games: number;
  winner: "home" | "away";
};

export type WttaScorePayload = {
  match_id: string;
  league: "WTTA";
  lines: ComputedLine[];
  result: ScoreResult;
  home_lines_won: number;
  away_lines_won: number;
  payload_version: 1;
};

/** Result of `report_score(dry_run=true)` — the review surface. */
export type ScoreDryRun = {
  ok: true;
  dry_run: true;
  match_id: string;
  score_result: ScoreResult;
  lines_summary: string;
  payload: WttaScorePayload;
  confirmation_token: string;
};

/** Result of `report_score(dry_run=false)` — the submit outcome. */
export type ScoreSubmit = {
  ok: true;
  dry_run: false;
  match_id: string;
  submitted: true;
  already_submitted?: boolean;
  wtta_confirmation?: string | null;
};

// ─── callTool — same gateway contract as lineup-api.ts ─────────────────
//
// Kept thin + local (mirrors lineup-api.ts; the gateway envelope is the
// shared base, `unwrapToolPayload` IS imported not duplicated). No
// retry / no caching — a one-shot human-driven flow owns those.

async function callTool<T>(
  tool: string,
  args: Record<string, unknown>,
): Promise<T> {
  let body: { ok: true; result: unknown };
  try {
    body = await apiPost<{ ok: true; result: unknown }>(
      `/api/tool/${tool}`,
      args,
    );
  } catch (e) {
    if (e instanceof ApiError) {
      // Gateway error body: `{ ok:false, error: {code,message} }` for
      // 502, or `{ ok:false, error: "<string>" }` for 401/404/429.
      const b = e.body as { error?: unknown } | null | undefined;
      let code = "GATEWAY_ERROR";
      if (b && typeof b === "object" && "error" in b) {
        const err = (b as { error: unknown }).error;
        if (typeof err === "string") code = err.toUpperCase();
        else if (err && typeof err === "object" && "code" in err) {
          const c = (err as { code?: unknown }).code;
          if (typeof c === "string") code = c;
        }
      }
      throw new LineupToolError(code, e.status, e.message);
    }
    throw new LineupToolError("NETWORK", 0, "Network error. Retry.");
  }

  const payload = unwrapToolPayload(body.result);
  if (!payload) {
    throw new LineupToolError(
      "MALFORMED_RESPONSE",
      200,
      "The server returned an unreadable response.",
    );
  }
  if (payload.ok === false) {
    const err = payload.error as
      | { code?: unknown; message?: unknown }
      | undefined;
    const code =
      err && typeof err.code === "string" ? err.code : "TOOL_ERROR";
    const message =
      err && typeof err.message === "string"
        ? err.message
        : "The tool reported an error.";
    throw new LineupToolError(code, 200, message);
  }
  return payload as T;
}

// ─── Public API ────────────────────────────────────────────────────────

/**
 * Phase 1 — build + validate the payload server-side and get back a
 * `confirmation_token` to review. NEVER submits (AGENTS.md rule 3).
 */
export function reportScoreDryRun(
  matchId: string,
  lines: ScoreLineInput[],
): Promise<ScoreDryRun> {
  return callTool<ScoreDryRun>("report_score", {
    match_id: matchId,
    lines,
    dry_run: true,
  });
}

/**
 * Phase 2 — the EXPLICIT human submit (AGENTS.md rule 3). Only fires
 * from the captain tapping "Submit to WTTA" AFTER a successful Review,
 * and only with the `confirmation_token` that Review returned. The
 * server re-derives the token from the submitted lines and refuses
 * (`CONFIRMATION_MISMATCH`) if the score changed since the dry run.
 */
export function reportScoreSubmit(
  matchId: string,
  lines: ScoreLineInput[],
  confirmationToken: string,
): Promise<ScoreSubmit> {
  return callTool<ScoreSubmit>("report_score", {
    match_id: matchId,
    lines,
    dry_run: false,
    confirmation_token: confirmationToken,
  });
}
